import { supabase } from '../lib/supabase';
import type { Tenant, SubscriptionPlan } from './tenantsApi';

export type { Tenant } from './tenantsApi';

export interface TenantUser {
  id: string;
  auth_user_id: string | null;
  email: string;
  full_name: string | null;
  role: string;
  is_active: boolean;
  last_login_at: string | null;
  created_at: string;
}

export interface TenantProject {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  is_active: boolean;
  is_default: boolean;
  created_at: string;
}

export interface SubscriptionUsageRecord {
  id: string;
  tenant_id: string;
  usage_month: string;
  ai_quiz_generated: number;
  ai_instant_win_generated: number;
  ai_game_contest_generated: number;
  created_at: string;
  updated_at: string;
}

export interface ImpersonationSession {
  id: string;
  admin_user_id: string;
  admin_email: string;
  impersonated_tenant_id: string;
  reason: string | null;
  started_at: string;
  ended_at: string | null;
  actions_count: number;
  created_at: string;
  tenant?: Tenant;
}

export interface ImpersonationLog {
  id: string;
  session_id: string;
  action_type: 'create' | 'update' | 'delete' | 'view' | 'export';
  resource_type: string;
  resource_id: string | null;
  action_details: Record<string, unknown>;
  created_at: string;
}

export interface TenantWithStats extends Tenant {
  user_count?: number;
  project_count?: number;
  quiz_count?: number;
  game_count?: number;
  last_activity?: string;
}

export interface PlatformStatsChanges {
  tenants_change: number;
  active_users_change: number;
  projects_change: number;
  quizzes_change: number;
  games_change: number;
  journeys_change: number;
}

export interface PlatformStats {
  total_tenants: number;
  active_tenants: number;
  total_users: number;
  active_users_30d: number;
  monthly_participants: number;
  total_projects: number;
  total_quizzes: number;
  total_games: number;
  total_journeys: number;
  plan_distribution: {
    free: number;
    premium: number;
    pro: number;
  };
  changes: PlatformStatsChanges;
}

export interface SubscriptionPlanDetails {
  id: string;
  name: string;
  display_name: string;
  price_monthly: string | null;
  price_yearly: string | null;
  features: Record<string, boolean>;
  limits: Record<string, number>;
  is_active: boolean;
  sort_order: number;
}

export interface PlatformHealthMetrics {
  total_requests: number;
  avg_response_time_ms: number;
  error_rate_percent: number;
  success_rate_percent: number;
}

export interface CustomerSearchParams {
  search?: string;
  plan?: SubscriptionPlan | 'all';
  status?: 'active' | 'suspended' | 'all';
  sortBy?: 'name' | 'created_at' | 'user_count';
  sortOrder?: 'asc' | 'desc';
  limit?: number;
  offset?: number;
}

class AdminApi {
  async startImpersonation(tenantId: string, reason?: string): Promise<{
    success: boolean;
    session_id?: string;
    tenant?: { id: string; name: string; domain: string };
    error?: string;
  }> {
    const { data, error } = await supabase
      .rpc('start_impersonation_session', {
        p_tenant_id: tenantId,
        p_reason: reason || null
      });

    if (error) throw error;
    return data;
  }

  async endImpersonation(sessionId?: string): Promise<{
    success: boolean;
    session_id?: string;
    duration_minutes?: number;
    actions_count?: number;
    error?: string;
  }> {
    const { data, error } = await supabase
      .rpc('end_impersonation_session', {
        p_session_id: sessionId || null
      });

    if (error) throw error;
    return data;
  }

  async getActiveImpersonation(): Promise<{
    active: boolean;
    session_id?: string;
    started_at?: string;
    reason?: string;
    actions_count?: number;
    tenant?: { id: string; name: string; domain: string };
  }> {
    const { data, error } = await supabase
      .rpc('get_active_impersonation_session');

    if (error) throw error;
    return data;
  }

  async logImpersonationAction(
    actionType: ImpersonationLog['action_type'],
    resourceType: string,
    resourceId?: string,
    actionDetails?: Record<string, unknown>
  ): Promise<{ logged: boolean; log_id?: string; session_id?: string }> {
    const { data, error } = await supabase
      .rpc('log_impersonation_action', {
        p_action_type: actionType,
        p_resource_type: resourceType,
        p_resource_id: resourceId || null,
        p_action_details: actionDetails || {}
      });

    if (error) throw error;
    return data;
  }

  async fetchImpersonationSessions(
    limit = 50,
    offset = 0
  ): Promise<ImpersonationSession[]> {
    const { data, error } = await supabase
      .from('admin_impersonation_sessions')
      .select(`
        *,
        tenant:tenants (
          id,
          name,
          domain,
          subscription_plan
        )
      `)
      .order('started_at', { ascending: false })
      .range(offset, offset + limit - 1);

    if (error) throw error;
    return data || [];
  }

  async fetchSessionLogs(sessionId: string): Promise<ImpersonationLog[]> {
    const { data, error } = await supabase
      .from('admin_impersonation_logs')
      .select('*')
      .eq('session_id', sessionId)
      .order('created_at', { ascending: false });

    if (error) throw error;
    return data || [];
  }

  async searchCustomers(params: CustomerSearchParams = {}): Promise<{
    customers: TenantWithStats[];
    total: number;
  }> {
    const {
      search = '',
      plan = 'all',
      status = 'all',
      sortBy = 'name',
      sortOrder = 'asc',
      limit = 20,
      offset = 0
    } = params;

    let query = supabase
      .from('tenants')
      .select('*', { count: 'exact' });

    if (search) {
      query = query.or(`name.ilike.%${search}%,domain.ilike.%${search}%`);
    }

    if (plan !== 'all') {
      query = query.eq('subscription_plan', plan);
    }

    if (status === 'active') {
      query = query.eq('is_active', true);
    } else if (status === 'suspended') {
      query = query.eq('is_active', false);
    }

    query = query.order(sortBy, { ascending: sortOrder === 'asc' });
    query = query.range(offset, offset + limit - 1);

    const { data, error, count } = await query;
    if (error) throw error;

    const customersWithStats = await Promise.all(
      (data || []).map(async (tenant) => {
        const [userCount, projectCount] = await Promise.all([
          this.getTenantUserCount(tenant.id),
          this.getTenantProjectCount(tenant.id)
        ]);

        return {
          ...tenant,
          user_count: userCount,
          project_count: projectCount
        };
      })
    );

    return {
      customers: customersWithStats,
      total: count || 0
    };
  }

  async getTenantUserCount(tenantId: string): Promise<number> {
    const { count, error } = await supabase
      .from('app_users')
      .select('*', { count: 'exact', head: true })
      .eq('tenant_id', tenantId)
      .eq('is_active', true);

    if (error) return 0;
    return count || 0;
  }

  async getTenantProjectCount(tenantId: string): Promise<number> {
    const { count, error } = await supabase
      .from('projects')
      .select('*', { count: 'exact', head: true })
      .eq('tenant_id', tenantId)
      .eq('is_active', true);

    if (error) return 0;
    return count || 0;
  }

  async getPlatformStats(): Promise<PlatformStats> {
    const startOfMonth = new Date();
    startOfMonth.setDate(1);
    startOfMonth.setHours(0, 0, 0, 0);
    const monthBoundary = startOfMonth.toISOString();

    const [
      tenantStats,
      userStats,
      monthlyParticipants,
      projectCount,
      quizCount,
      gameCount,
      journeyCount,
      planDistribution,
      prevTenants,
      prevActiveUsers,
      prevProjects,
      prevQuizzes,
      prevGames,
      prevJourneys
    ] = await Promise.all([
      this.getTenantStats(),
      this.getUserStats(),
      this.getMonthlyParticipants(),
      this.getTableCount('projects'),
      this.getTableCount('quizzes'),
      this.getTableCount('ai_generated_games'),
      this.getTableCount('journey'),
      this.getPlanDistribution(),
      this.getTableCountBefore('tenants', monthBoundary),
      this.getPreviousActiveUsers30d(),
      this.getTableCountBefore('projects', monthBoundary),
      this.getTableCountBefore('quizzes', monthBoundary),
      this.getTableCountBefore('ai_generated_games', monthBoundary),
      this.getTableCountBefore('journey', monthBoundary)
    ]);

    return {
      total_tenants: tenantStats.total,
      active_tenants: tenantStats.active,
      total_users: userStats.total,
      active_users_30d: userStats.active30d,
      monthly_participants: monthlyParticipants,
      total_projects: projectCount,
      total_quizzes: quizCount,
      total_games: gameCount,
      total_journeys: journeyCount,
      plan_distribution: planDistribution,
      changes: {
        tenants_change: this.computeChange(tenantStats.total, prevTenants),
        active_users_change: this.computeChange(userStats.active30d, prevActiveUsers),
        projects_change: this.computeChange(projectCount, prevProjects),
        quizzes_change: this.computeChange(quizCount, prevQuizzes),
        games_change: this.computeChange(gameCount, prevGames),
        journeys_change: this.computeChange(journeyCount, prevJourneys)
      }
    };
  }

  async getTenantStats(): Promise<{ total: number; active: number }> {
    const { count: total } = await supabase
      .from('tenants')
      .select('*', { count: 'exact', head: true });

    const { count: active } = await supabase
      .from('tenants')
      .select('*', { count: 'exact', head: true })
      .eq('is_active', true);

    return { total: total || 0, active: active || 0 };
  }

  async getUserStats(): Promise<{ total: number; active30d: number }> {
    const { count: total } = await supabase
      .from('app_users')
      .select('*', { count: 'exact', head: true })
      .eq('status', 'active');

    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

    const { count: active30d } = await supabase
      .from('app_users')
      .select('*', { count: 'exact', head: true })
      .eq('status', 'active')
      .gte('last_active', thirtyDaysAgo.toISOString());

    return { total: total || 0, active30d: active30d || 0 };
  }

  async getMonthlyParticipants(): Promise<number> {
    const startOfMonth = new Date();
    startOfMonth.setDate(1);
    startOfMonth.setHours(0, 0, 0, 0);

    const { count, error } = await supabase
      .from('tenant_users')
      .select('*', { count: 'exact', head: true })
      .gte('last_active_at', startOfMonth.toISOString());

    if (error) return 0;
    return count || 0;
  }

  async getTableCount(tableName: string): Promise<number> {
    const { count, error } = await supabase
      .from(tableName)
      .select('*', { count: 'exact', head: true });

    if (error) return 0;
    return count || 0;
  }

  async getTableCountBefore(tableName: string, beforeDate: string): Promise<number> {
    const { count, error } = await supabase
      .from(tableName)
      .select('*', { count: 'exact', head: true })
      .lt('created_at', beforeDate);

    if (error) return 0;
    return count || 0;
  }

  private computeChange(current: number, previous: number): number {
    if (previous === 0 && current === 0) return 0;
    if (previous === 0) return 100;
    return Math.round(((current - previous) / previous) * 1000) / 10;
  }

  async getPreviousActiveUsers30d(): Promise<number> {
    const sixtyDaysAgo = new Date();
    sixtyDaysAgo.setDate(sixtyDaysAgo.getDate() - 60);
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

    const { count, error } = await supabase
      .from('app_users')
      .select('*', { count: 'exact', head: true })
      .eq('status', 'active')
      .gte('last_active', sixtyDaysAgo.toISOString())
      .lt('last_active', thirtyDaysAgo.toISOString());

    if (error) return 0;
    return count || 0;
  }

  async getPlanDistribution(): Promise<{ free: number; premium: number; pro: number }> {
    const { data, error } = await supabase
      .from('tenants')
      .select('subscription_plan')
      .eq('is_active', true);

    if (error || !data) {
      return { free: 0, premium: 0, pro: 0 };
    }

    const distribution = { free: 0, premium: 0, pro: 0 };
    data.forEach(tenant => {
      const plan = tenant.subscription_plan as SubscriptionPlan;
      if (plan in distribution) {
        distribution[plan]++;
      }
    });

    return distribution;
  }

  async getSubscriptionPlans(): Promise<SubscriptionPlanDetails[]> {
    const { data, error } = await supabase
      .from('subscription_plans')
      .select('*')
      .eq('is_active', true)
      .order('sort_order', { ascending: true });

    if (error) throw error;
    return data || [];
  }

  async updateTenantPlan(tenantId: string, plan: SubscriptionPlan): Promise<void> {
    const { error } = await supabase
      .from('tenants')
      .update({
        subscription_plan: plan,
        subscription_status: 'active',
        subscription_start_date: new Date().toISOString(),
      })
      .eq('id', tenantId);

    if (error) throw error;
  }

  async suspendTenant(tenantId: string): Promise<void> {
    const { error } = await supabase
      .from('tenants')
      .update({ is_active: false })
      .eq('id', tenantId);

    if (error) throw error;
  }

  async activateTenant(tenantId: string): Promise<void> {
    const { error } = await supabase
      .from('tenants')
      .update({ is_active: true })
      .eq('id', tenantId);

    if (error) throw error;
  }

  async getTenantDetails(tenantId: string): Promise<TenantWithStats | null> {
    const { data: tenant, error } = await supabase
      .from('tenants')
      .select('*')
      .eq('id', tenantId)
      .maybeSingle();

    if (error || !tenant) return null;

    const [userCount, projectCount, quizCount, gameCount] = await Promise.all([
      this.getTenantUserCount(tenantId),
      this.getTenantProjectCount(tenantId),
      this.getTenantQuizCount(tenantId),
      this.getTenantGameCount(tenantId)
    ]);

    return {
      ...tenant,
      user_count: userCount,
      project_count: projectCount,
      quiz_count: quizCount,
      game_count: gameCount
    };
  }

  async getTenantQuizCount(tenantId: string): Promise<number> {
    const { count, error } = await supabase
      .from('quizzes')
      .select('*', { count: 'exact', head: true })
      .eq('tenant_id', tenantId);

    if (error) return 0;
    return count || 0;
  }

  async getTenantGameCount(tenantId: string): Promise<number> {
    const { count, error } = await supabase
      .from('ai_generated_games')
      .select('*', { count: 'exact', head: true })
      .eq('tenant_id', tenantId);

    if (error) return 0;
    return count || 0;
  }

  async getPlatformHealthMetrics(days: number): Promise<PlatformHealthMetrics> {
    const { data, error } = await supabase.rpc('get_platform_health_metrics', {
      p_days: days
    });

    if (error || !data) {
      return {
        total_requests: 0,
        avg_response_time_ms: 0,
        error_rate_percent: 0,
        success_rate_percent: 0
      };
    }

    return data as PlatformHealthMetrics;
  }

  async getAllTenants(): Promise<Tenant[]> {
    const { data, error } = await supabase
      .from('tenants')
      .select('id, name, domain, is_active')
      .order('name', { ascending: true });

    if (error) throw error;
    return data || [];
  }

  async getTenantUsers(tenantId: string): Promise<TenantUser[]> {
    const { data, error } = await supabase
      .from('app_users')
      .select('id, auth_user_id, email, full_name, role, is_active, last_login_at, created_at')
      .eq('tenant_id', tenantId)
      .order('created_at', { ascending: false });

    if (error) throw error;
    return data || [];
  }

  async getTenantProjects(tenantId: string): Promise<TenantProject[]> {
    const { data, error } = await supabase
      .from('projects')
      .select('id, name, slug, description, is_active, is_default, created_at')
      .eq('tenant_id', tenantId)
      .order('is_default', { ascending: false })
      .order('created_at', { ascending: false });

    if (error) throw error;
    return data || [];
  }

  async getTenantUsageHistory(tenantId: string, monthsBack = 12): Promise<SubscriptionUsageRecord[]> {
    const startDate = new Date();
    startDate.setMonth(startDate.getMonth() - monthsBack);
    const startMonth = startDate.toISOString().slice(0, 7) + '-01';

    const { data, error } = await supabase
      .from('subscription_usage')
      .select('*')
      .eq('tenant_id', tenantId)
      .gte('usage_month', startMonth)
      .order('usage_month', { ascending: false });

    if (error) throw error;
    return data || [];
  }
}

export const adminApi = new AdminApi();
